import { forwardRef } from "react";

import { ChevronRightIcon } from "@eisox/icons";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Breadcrumb
 * -----------------------------------------------------------------------------------------------*/

type BreadcrumbProps = React.ComponentPropsWithRef<"nav">;

const Root = forwardRef<HTMLElement, BreadcrumbProps>((props, forwardedRef) => (
  <nav {...props} ref={forwardedRef} aria-label="breadcrumb" />
));

Root.displayName = "Breadcrumb";

/* -------------------------------------------------------------------------------------------------
 * List
 * -----------------------------------------------------------------------------------------------*/

type BreadcrumbListProps = React.ComponentPropsWithRef<"ol">;

const List = forwardRef<HTMLOListElement, BreadcrumbListProps>(({ className, ...props }, forwardedRef) => (
  <ol
    {...props}
    ref={forwardedRef}
    className={cn("mb-0 gap-1.25 flex items-center text-base text-primary-dark-gray", className)}
  />
));

List.displayName = "BreadcrumbList";

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

type BreadcrumbItemProps = React.ComponentPropsWithRef<"li">;

const Item = forwardRef<HTMLLIElement, BreadcrumbItemProps>(({ className, ...props }, forwardedRef) => (
  <li
    {...props}
    ref={forwardedRef}
    className={cn("gap-1.25 inline-flex items-center overflow-hidden text-ellipsis whitespace-nowrap", className)}
  />
));

Item.displayName = "BreadcrumbItem";

/* -------------------------------------------------------------------------------------------------
 * Link
 * -----------------------------------------------------------------------------------------------*/

interface BreadcrumbLinkProps extends React.ComponentPropsWithRef<"a"> {
  asChild?: boolean;
}

const Link = forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(({ asChild, className, ...props }, forwardedRef) => {
  const Comp = asChild ? Slot : "a";
  return <Comp {...props} ref={forwardedRef} className={cn("cursor-pointer", className)} />;
});

Link.displayName = "BreadcrumbLink";

/* -------------------------------------------------------------------------------------------------
 * Page
 * -----------------------------------------------------------------------------------------------*/

interface BreadcrumbPageProps extends React.ComponentPropsWithRef<"span"> {
  asChild?: boolean;
}

const Page = forwardRef<HTMLSpanElement, BreadcrumbPageProps>(({ className, asChild, ...props }, forwardedRef) => {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      {...props}
      ref={forwardedRef}
      className={cn("text-primary-dark-blue", className)}
      role="link"
      aria-disabled="true"
      aria-current="page"
    />
  );
});

Page.displayName = "BreadcrumbPage";

/* -------------------------------------------------------------------------------------------------
 * Separator
 * -----------------------------------------------------------------------------------------------*/

type BreadcrumbSeparatorProps = React.ComponentPropsWithRef<"li">;

const Separator = forwardRef<HTMLLIElement, BreadcrumbSeparatorProps>(
  ({ className, children, ...props }, forwardedRef) => (
    <li
      {...props}
      ref={forwardedRef}
      className={cn("[&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:align-middle", className)}
      role="presentation"
      aria-hidden
    >
      {children ?? <ChevronRightIcon />}
    </li>
  ),
);

Separator.displayName = "BreadcrumbSeparator";

export { Item, Link, List, Page, Root, Separator };
export type {
  BreadcrumbItemProps,
  BreadcrumbLinkProps,
  BreadcrumbListProps,
  BreadcrumbPageProps,
  BreadcrumbProps,
  BreadcrumbSeparatorProps,
};
